import { useEffect, useRef, useState, type MouseEvent } from 'react';

import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import DownloadIcon from '@cartwhl/ui/icons/DownloadIcon';
import PersonIcon from '@cartwhl/ui/icons/PersonIcon';
import UncheckedIcon from '@cartwhl/ui/icons/UncheckedIcon';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';
import Button from '@cartwhl/ui/input/Button';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';

import type { MotionStatus } from '@cartwhl/common/api/getMotionsByGroup';

import BatchMotionThumbnailLoadingChip from './BatchMotionThumbnailLoadingChip';
import { batchMotionFileCanPreview as fileCanPreview } from './BatchMotionJobPreviewModal';

/** Matches preview modal small icon controls (white tile on light card). */
const THUMB_OVERLAY_ICON_BUTTON =
  '!bg-white dark:!bg-gray-90 !border-gray-30 dark:!border-gray-75 border hover:!bg-gray-15 dark:hover:!bg-gray-75 h-9 w-9 shrink-0 p-0';

const THUMB_ERROR_INDICATOR_WRAP =
  'pointer-events-none absolute right-2 bottom-2 z-20 h-9 w-9 shrink-0';
const THUMB_ERROR_INDICATOR_BASE =
  'absolute inset-0 rounded-lg bg-white';
const THUMB_ERROR_INDICATOR_TINT =
  'absolute inset-0 flex items-center justify-center rounded-lg bg-red/10 dark:bg-red/10';

/** Row shape for batch processing thumbnail grids (Comic / Generation). */
export type BatchMotionThumbnailGridFile = {
  id: string;
  name: string;
  status?: MotionStatus;
  errorMessage?: string;
  bvhURL?: string;
  bvhURLs?: string[];
  /** Square studio / history preview MP4 (post-processor). */
  studioPreviewURL?: string;
  cwmsURL?: string;
};

const PROJECTS_GRID =
  'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:px-0';

function isFailedStatus(status?: MotionStatus) {
  return status === 'FAILED' || status === 'CANCELLED';
}

/** Prefer studio preview MP4; keep cwms as fallback where the API only returns that URL for the tile. */
function tilePreviewVideoUrl(file: BatchMotionThumbnailGridFile): string {
  const studio = file.studioPreviewURL?.trim();
  if (studio) return studio;
  return file.cwmsURL?.trim() ?? '';
}

function pathOnly(url: string): string | null {
  try {
    return new URL(url).pathname;
  } catch {
    return null;
  }
}

/**
 * Thumbnail preview MP4: avoid reloading on every poll when only the presigned
 * query string changes and playback is healthy; after a load error, accept a
 * new URL for the same object path so a late-uploaded MP4 can appear.
 * Pauses when off-screen to reduce GPU pressure (many tiles + WebGL elsewhere).
 */
function BatchMotionThumbnailVideo({
  candidateUrl,
  onLoadError,
}: {
  candidateUrl: string;
  onLoadError?: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !candidateUrl) return;

    const cur = el.currentSrc || el.src || '';
    if (cur === candidateUrl) return;

    const nextPath = pathOnly(candidateUrl);
    const curPath = cur ? pathOnly(cur) : null;
    const sameObjectPath =
      nextPath !== null && curPath !== null && nextPath === curPath;

    if (sameObjectPath && !el.error) {
      return;
    }

    el.src = candidateUrl;
  }, [candidateUrl]);

  useEffect(() => {
    const root = containerRef.current;
    const el = videoRef.current;
    if (!root || !el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const vis = entries.some((e) => e.isIntersecting);
        if (vis) {
          void el.play().catch(() => {
            /* autoplay may be blocked; ignore */
          });
        } else {
          el.pause();
        }
      },
      { root: null, rootMargin: '64px', threshold: 0.01 },
    );
    io.observe(root);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="h-full w-full">
      <video
        ref={videoRef}
        className="h-full w-full bg-gray-100 object-cover"
        muted
        loop
        autoPlay
        playsInline
        preload="metadata"
        crossOrigin="anonymous"
        onError={() => {
          onLoadError?.();
        }}
      />
    </div>
  );
}

function ThumbnailLoadingPlaceholder() {
  return (
    <div className="bg-gray-30 dark:bg-gray-70 relative h-full w-full">
      <BatchMotionThumbnailLoadingChip />
    </div>
  );
}

/** Same empty state as studio ProjectCard when there is no preview asset. */
function ThumbnailProjectsBlank() {
  return (
    <div className="bg-gray-30 dark:bg-gray-70 flex h-full w-full items-center justify-center">
      <PersonIcon className="text-gray-70 dark:text-gray-45 h-8 w-8" />
    </div>
  );
}

function BatchMotionJobThumbnailCard({
  file,
  motionNumber,
  onOpenPreview,
  allowErrorExpand,
  selectionEnabled,
  isSelected,
  onCheckboxClick,
  onDownloadClick,
  downloadBusy,
  canDownload,
  errorThumbnailVariant = 'default',
  onErrorThumbnailClick,
}: {
  file: BatchMotionThumbnailGridFile;
  motionNumber: number;
  onOpenPreview?: () => void;
  /** When true, failed rows with an error message toggle details on card click. */
  allowErrorExpand?: boolean;
  selectionEnabled?: boolean;
  isSelected?: boolean;
  onCheckboxClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onDownloadClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  downloadBusy?: boolean;
  /** When false, hides the hover download control (e.g. exports not ready yet). */
  canDownload?: boolean;
  /** Comic errors tab: gray tile, warning top-right, opens error modal on click. */
  errorThumbnailVariant?: 'default' | 'comic-errors';
  onErrorThumbnailClick?: (jobId: string) => void;
}) {
  const [errorExpanded, setErrorExpanded] = useState(false);
  const [videoLoadFailed, setVideoLoadFailed] = useState(false);
  const isMobile = useMediaQuery('(max-width: 640px)');

  const previewSrc = tilePreviewVideoUrl(file);
  // `onOpenPreview` is only passed when the grid has already determined the
  // file is previewable (via `fileCanPreview` or a caller-supplied predicate),
  // so use its presence as the source of truth rather than re-running the check.
  const canPreview = Boolean(onOpenPreview);
  const failed = isFailedStatus(file.status);
  const complete = file.status === 'COMPLETED';
  const comicErrorsLayout =
    errorThumbnailVariant === 'comic-errors' && failed;

  useEffect(() => {
    setVideoLoadFailed(false);
  }, [previewSrc]);

  const titleText = `${String(motionNumber).padStart(2, '0')}. ${file.name}`;

  const handleCardClick = (e: MouseEvent<HTMLDivElement>) => {
    const t = e.target as HTMLElement;
    if (
      t.closest('button[data-batch-thumb-checkbox]') ||
      t.closest('button[data-batch-thumb-download]')
    ) {
      return;
    }
    if (comicErrorsLayout && onErrorThumbnailClick) {
      onErrorThumbnailClick(file.id);
      return;
    }
    if (canPreview && onOpenPreview) {
      onOpenPreview();
      return;
    }
    if (allowErrorExpand && failed && file.errorMessage) {
      setErrorExpanded((v) => !v);
    }
  };

  const interactive =
    (comicErrorsLayout && Boolean(onErrorThumbnailClick)) ||
    (canPreview && Boolean(onOpenPreview)) ||
    (allowErrorExpand && failed && Boolean(file.errorMessage));

  const showThumbnailVideo =
    complete && Boolean(previewSrc) && !videoLoadFailed;

  const showProjectsBlank = complete && (!previewSrc || videoLoadFailed);

  const showSelection = Boolean(
    selectionEnabled && complete && onCheckboxClick,
  );
  const showDownload = Boolean(
    complete && onDownloadClick && canDownload !== false,
  );
  const showComicErrorBadge = comicErrorsLayout;

  return (
    <div
      className={`group relative flex flex-col gap-4 rounded-2xl p-2 pb-6 transition-colors select-none ${
        isSelected
          ? 'bg-blue/10 dark:bg-blue/20'
          : interactive
            ? 'cursor-pointer hover:bg-gray-15 dark:hover:bg-gray-75'
            : 'cursor-default'
      }`}
      onClick={handleCardClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (comicErrorsLayout && onErrorThumbnailClick) {
                  onErrorThumbnailClick(file.id);
                } else if (canPreview && onOpenPreview) onOpenPreview();
                else if (allowErrorExpand && failed && file.errorMessage) {
                  setErrorExpanded((v) => !v);
                }
              }
            }
          : undefined
      }
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={
        comicErrorsLayout && onErrorThumbnailClick
          ? `Error details for ${file.name}`
          : undefined
      }
    >
      <div className="relative aspect-video overflow-hidden rounded-xl">
        {showSelection ? (
          <button
            type="button"
            data-batch-thumb-checkbox
            onClick={(e) => {
              e.stopPropagation();
              onCheckboxClick?.(e);
            }}
            className={`absolute left-2 top-2 z-30 transition-opacity ${
              isSelected
                ? 'opacity-100'
                : isMobile
                  ? 'opacity-100'
                  : 'opacity-0 group-hover:opacity-100'
            }`}
            aria-label={isSelected ? 'Deselect motion' : 'Select motion'}
          >
            {isSelected ? (
              <div className="relative flex h-8 w-8 items-center justify-center">
                <UncheckedIcon className="stroke-blue [&>rect]:fill-blue absolute inset-0 h-8 w-8 [&>rect]:[rx:64] [&>rect]:[ry:64]" />
                <CheckmarkIcon className="relative h-5 w-5 text-white" />
              </div>
            ) : (
              <UncheckedIcon className="stroke-gray-45 dark:stroke-gray-30 dark:[&>rect]:fill-gray-75 h-8 w-8 [&>rect]:fill-white [&>rect]:[rx:64] [&>rect]:[ry:64]" />
            )}
          </button>
        ) : null}
        {showComicErrorBadge ? (
          <div className={THUMB_ERROR_INDICATOR_WRAP} aria-hidden>
            <div className={THUMB_ERROR_INDICATOR_BASE} />
            <div className={THUMB_ERROR_INDICATOR_TINT}>
              <WarningIcon className="text-red h-5 w-5 shrink-0" />
            </div>
          </div>
        ) : null}
        {showDownload ? (
          <Button
            type="button"
            data-batch-thumb-download
            variant="secondary"
            size="lg"
            className={`absolute right-2 top-2 z-20 transition-opacity ${THUMB_OVERLAY_ICON_BUTTON} ${
              isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}
            disabled={downloadBusy}
            aria-label={`Download ${file.name}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDownloadClick?.(e);
            }}
            icon={
              downloadBusy ? (
                <Loading className="text-gray-60 dark:text-gray-45 h-4 w-4" />
              ) : (
                <DownloadIcon className="h-4 w-4" />
              )
            }
          />
        ) : null}
        <div className="hover:shadow-menu block h-full w-full select-none">
          {failed ? (
            <ThumbnailProjectsBlank />
          ) : showThumbnailVideo ? (
            <BatchMotionThumbnailVideo
              candidateUrl={previewSrc}
              onLoadError={() => setVideoLoadFailed(true)}
            />
          ) : showProjectsBlank ? (
            <ThumbnailProjectsBlank />
          ) : (
            <ThumbnailLoadingPlaceholder />
          )}
        </div>
      </div>
      <div className="min-w-0 px-2 select-none">
        <Text variant="studio/body/sm" className="truncate select-none">
          {titleText}
        </Text>
        {errorExpanded && file.errorMessage && !comicErrorsLayout ? (
          <Text variant="studio/body/sm" className="text-red pt-1 select-none">
            {file.errorMessage}
          </Text>
        ) : null}
      </div>
    </div>
  );
}

export function BatchMotionJobThumbnailGrid({
  files,
  onPreviewJob,
  allowErrorExpand,
  selectionEnabled,
  selectedJobIds,
  onCheckboxClick,
  onDownloadJob,
  downloadingJobId,
  errorThumbnailVariant = 'default',
  onErrorThumbnailClick,
  /** When set (same length as `files`), used as the displayed motion index instead of 1..n within this slice. */
  motionNumbers,
  canPreviewFile,
  canDownloadFile,
}: {
  files: BatchMotionThumbnailGridFile[];
  onPreviewJob: (jobId: string) => void;
  allowErrorExpand?: boolean;
  /** When true, completed jobs show a projects-style selection checkbox. */
  selectionEnabled?: boolean;
  selectedJobIds?: ReadonlySet<string>;
  /** Receives job id and the originating checkbox event (for shift / meta range). */
  onCheckboxClick?: (jobId: string, event: MouseEvent<HTMLButtonElement>) => void;
  /** Per-tile zip download (same as preview modal per-body download). */
  onDownloadJob?: (jobId: string, event: MouseEvent<HTMLButtonElement>) => void;
  downloadingJobId?: string | null;
  errorThumbnailVariant?: 'default' | 'comic-errors';
  onErrorThumbnailClick?: (jobId: string) => void;
  motionNumbers?: number[];
  /**
   * Override the default preview gate (`batchMotionFileCanPreview`) with a
   * custom predicate. Useful for applets (e.g. Mascot) whose files don't have
   * BVH URLs but do have a direct video URL to preview.
   */
  canPreviewFile?: (file: BatchMotionThumbnailGridFile) => boolean;
  /** When set, per-tile download is shown only when this returns true (e.g. zip exports ready). */
  canDownloadFile?: (file: BatchMotionThumbnailGridFile) => boolean;
}) {
  const selected = selectedJobIds ?? new Set<string>();
  const resolveCanPreview = canPreviewFile ?? fileCanPreview;

  return (
    <div className={PROJECTS_GRID}>
      {files.map((file, index) => (
        <BatchMotionJobThumbnailCard
          key={file.id}
          file={file}
          motionNumber={motionNumbers?.[index] ?? index + 1}
          allowErrorExpand={allowErrorExpand}
          selectionEnabled={selectionEnabled}
          isSelected={selected.has(file.id)}
          onCheckboxClick={
            onCheckboxClick
              ? (e) => onCheckboxClick(file.id, e)
              : undefined
          }
          onDownloadClick={
            onDownloadJob ? (e) => onDownloadJob(file.id, e) : undefined
          }
          canDownload={canDownloadFile ? canDownloadFile(file) : true}
          downloadBusy={downloadingJobId === file.id}
          errorThumbnailVariant={errorThumbnailVariant}
          onErrorThumbnailClick={onErrorThumbnailClick}
          onOpenPreview={
            resolveCanPreview(file)
              ? () => {
                  onPreviewJob(file.id);
                }
              : undefined
          }
        />
      ))}
    </div>
  );
}
